import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import Page from '../components/Page';

export default function TermsPage() {
    useEffect(() => {
        ReactGA.send('pageview');
    }, []);

    return (
        <Page seo={{
            title: 'Terms & Conditions — Scheduly',
            description: 'Read the Scheduly Terms and Conditions for using our church scheduling and volunteer management software.',
            canonical: 'https://scheduly.org/terms',
        }}>
            <div className="max-w-3xl mx-auto px-6 py-16">
                <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Terms & Conditions</h1>
                <p className="text-sm text-slate-500 mb-10">Last updated: April 1, 2026</p>

                <div className="prose prose-slate max-w-none space-y-8 text-sm text-slate-700 leading-relaxed">

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">1. Acceptance of Terms</h2>
                        <p>By accessing or using Scheduly ("Service"), you agree to be bound by these Terms and Conditions. If you do not agree, please do not use the Service.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">2. Description of Service</h2>
                        <p>Scheduly is a church scheduling and volunteer management platform. We provide tools for event planning, volunteer coordination, member communication, and reporting. The Service is offered on a subscription basis.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">3. User Accounts</h2>
                        <p>You are responsible for maintaining the confidentiality of your account credentials. You agree to provide accurate, current, and complete information when creating an account. You must notify us immediately of any unauthorized use of your account.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">4. Free Trial</h2>
                        <p>We offer a 3-month free trial period for new subscribers. At the end of the trial period, your subscription will automatically convert to a paid plan at the rate applicable to your selected plan unless you cancel before the trial ends.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">5. Billing and Payments</h2>
                        <p>Subscriptions are billed on a monthly basis. All payments are processed securely through Stripe. By providing payment information, you authorize us to charge your payment method for the subscription fee each billing period. Prices are subject to change with reasonable notice.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">6. Cancellation and Refunds</h2>
                        <p>You may cancel your subscription at any time from your Account page. Cancellation takes effect at the end of the current billing period. We offer a 30-day money-back guarantee on your first paid billing period — if you are not satisfied within the first 30 days of paid service, contact us for a full refund. No questions asked.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">7. SMS and Email Credits</h2>
                        <p>Each plan includes a monthly allotment of SMS and email credits. Credits are consumed when notifications are sent. Unused credits partially roll over each month (up to 50% of the monthly allotment). If you exceed your credit limit, overdraft rates apply as described on the Pricing page. Credits have no cash value and are non-refundable.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">8. Acceptable Use</h2>
                        <p>You agree to use the Service only for lawful purposes. You may not use the Service to send spam, harass individuals, distribute illegal content, or engage in any activity that violates applicable law. We reserve the right to suspend or terminate accounts that violate these terms.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">9. Data and Privacy</h2>
                        <p>Your use of the Service is also governed by our <a href="/privacy" className="text-indigo-600 underline">Privacy Policy</a>, which is incorporated into these Terms by reference.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">10. Intellectual Property</h2>
                        <p>All content, features, and functionality of the Service are owned by Scheduly and are protected by applicable intellectual property laws. You retain ownership of the data you input into the Service.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">11. Disclaimer of Warranties</h2>
                        <p>The Service is provided "as is" without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, error-free, or free of viruses or other harmful components.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">12. Limitation of Liability</h2>
                        <p>To the maximum extent permitted by law, Scheduly shall not be liable for any indirect, incidental, special, or consequential damages arising out of or in connection with your use of the Service. Our total liability to you shall not exceed the amount paid by you in the three months preceding the claim.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">13. Changes to Terms</h2>
                        <p>We reserve the right to modify these Terms at any time. We will notify you of material changes by email or by posting a notice on the Service. Continued use of the Service after changes constitutes acceptance of the revised Terms.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">14. Contact</h2>
                        <p>For questions about these Terms, contact us at <a href="mailto:schedulyevents@gmail.com" className="text-indigo-600 underline">schedulyevents@gmail.com</a>.</p>
                    </section>

                </div>
            </div>
        </Page>
    );
}
